import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { notify } from '../../helpers/toastify';

import { GET_PUBLIC_CLIENT_ACTIVITY } from '@bloomays-lib/adapter.api-bloomer';

import { FallbackError, WithErrorBoundary, LoaderSkeleton } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { getApolloErrorCode } from '../../helpers/error';
import { IActivity, IMission } from '@bloomays-lib/types.shared';
import CRAValidation from '../organisms/CRAValidation';
import { ActivitySummary } from '../../types/components';
import { activityToActivitySummary } from '../../helpers/CRA';
import { styled } from '@mui/material/styles';

const ViewSignedCRA = () => {
  const [activitySummary, setActivitySummary] = useState<ActivitySummary>();
  const { id: signingDocumentId } = useParams<{ id: string }>();
  const { t } = useTranslation(['random', 'notify']);
  const [activityNotFound, setActivityNotFound] = useState<boolean>(false);

  const [getPublicClientActivity, { loading: loadingClientActivity, error: errorClientActivity }] = useLazyQuery<
    { getPublicClientActivity: IActivity },
    { signingDocumentID: string }
  >(GET_PUBLIC_CLIENT_ACTIVITY);

  const handleError = useCallback(
    (error: ApolloError) => {
      const code = getApolloErrorCode(error);
      switch (code) {
        case 'TOKEN_EXPIRED':
          notify('warning', t('expiredLink', { ns: 'notify' }), undefined, {
            autoClose: false,
            hideProgressBar: true,
          });
          break;
        case 'ALREADY_SIGNED':
          notify('info', t('craAlreadySigned', { ns: 'notify' }));
          break;
        case 'OBJECT_NOT_FOUND':
          notify('warning', t('invalidInviteLink', { ns: 'notify' }));
          setActivityNotFound(true);
          break;
        default:
          notify('error', t('linkError', { ns: 'notify' }), error);
      }
    },
    [setActivityNotFound, t],
  );

  useEffect(() => {
    (async () => {
      if (signingDocumentId) {
        try {
          const res = await getPublicClientActivity({
            context: { isPublic: true },
            variables: {
              signingDocumentID: signingDocumentId,
            },
          });
          if (res?.data?.getPublicClientActivity) {
            setActivitySummary(
              activityToActivitySummary(
                res.data.getPublicClientActivity,
                res.data.getPublicClientActivity.mission as IMission,
              ),
            );
          }
        } catch (e) {
          console.warn('Handled error in useEffect to get embed cra invite', e);
          return;
        }
      }
    })();
  }, [getPublicClientActivity, signingDocumentId]);

  useEffect(() => {
    if (errorClientActivity) {
      handleError(errorClientActivity);
    }
    return;
  }, [errorClientActivity, handleError]);

  if (activityNotFound) {
    return <FallbackError hideRedirection={true} errorText={t('invalidDoc', { ns: 'notify' })} />;
  }
  if (loadingClientActivity || !activitySummary)
    return <LoaderSkeleton label={t('loadingText', { ns: 'random' })} height={600} width={800} />;

  return (
    <Container>
      <CRAValidation activitySummary={activitySummary} readOnly={true} />
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  margin: 'auto',
  width: '90%',
}));

export default WithErrorBoundary(ViewSignedCRA);
